import styled from 'styled-components';
import { AdvancedImage } from '@cloudinary/react';

// Components
import { Typography } from '../../display/Typography';
import { Definition } from '../../display/Definition';

export const Container = styled.div<{ isSelected?: boolean }>`
  display: flex;
  flex-direction: column;
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  cursor: pointer;
  position: relative;
  box-shadow: ${({ theme }) => theme.shadows.dropdown};
  border: ${({ isSelected, theme }) =>
    isSelected ? `0.3rem solid ${theme.colors.garnet}` : 'none'};
  transition: border 0.2s ease;
`;

export const CloseIcon = styled.button`
  display: none;
  position: absolute;
  background: transparent;
  top: 2rem;
  right: 2rem;
  border: 0;
  padding: 0;
  z-index: 999;

  img {
    width: 2.5rem;
    height: 2.5rem;
  }
`;

/**
 * Marco de la zona de imagen. `overflow: hidden` es OBLIGATORIO: recorta el
 * `scale(1.1)` + `filter: blur()` de `LqipLayer` exactamente a 20rem, para
 * que el difuminado NO se desborde sobre el título, los bordes ni las
 * esquinas redondeadas de la tarjeta. Fija la altura para que el layout del
 * grid no dependa de la carga de la imagen.
 */
export const ImageFrame = styled.div`
  position: relative;
  width: 100%;
  height: 20rem;
  overflow: hidden;
`;

/**
 * Placeholder blur-up: imagen sharp diminuta (w_32) difuminada por el
 * NAVEGADOR con `filter: blur()` (cero blur en servidor). `scale(1.1)`
 * tapa el borde transparente que deja el blur (queda recortado por
 * `ImageFrame`). Va DETRÁS de la imagen real (z-index 0) y `alt=""` para
 * que el selector E2E `img:not([alt])` siga apuntando solo a la
 * `<AdvancedImage>` real.
 */
export const LqipLayer = styled.img`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  filter: blur(16px);
  transform: scale(1.1);
  z-index: 0;
  pointer-events: none;
`;

export const CloudinaryAdvanceImage = styled(AdvancedImage)`
  position: relative;
  z-index: 1;
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

export const TextContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.xxs};
  padding: ${({ theme }) => theme.spacing.xxs}
    ${({ theme }) => theme.spacing.xs};
  background-color: ${({ theme }) => theme.colors.backgroundLila};
`;

export const Text = styled(Typography)`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const ContainerInfo = styled.div`
  display: none;
`;

export const StyledDefinition = styled(Definition)`
  & * {
    font-size: ${({ theme }) => theme.fontSize.XS};
  }
`;

export const CheckboxContainer = styled.div`
  position: absolute;
  top: 1rem;
  left: 1rem;
  z-index: 10;
  background-color: ${({ theme }) => theme.colors.white};
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  padding: 0.4rem;
  box-shadow: ${({ theme }) => theme.shadows.dropdown};
  display: flex;
  align-items: center;
  justify-content: center;
`;

/**
 * Papelera de la foto. Es el botón de borrar canónico de la app
 * (`ButtonIconBackground variant="delete"`): el mismo icono blanco
 * `remove-icon.svg` sobre un círculo granate.
 *
 * ⚠️ NO copiar aquí el fondo blanco del `CheckboxContainer` de al lado: ese
 * icono es blanco, así que sobre blanco desaparece — la papelera quedaba
 * dibujada y era invisible (cazado en navegador). El círculo granate además la
 * separa de la casilla de selección, que hace otra cosa completamente distinta.
 */
export const DeleteContainer = styled.div`
  position: absolute;
  top: 1rem;
  right: 1rem;
  z-index: 10;
  background-color: ${({ theme }) => theme.colors.garnet};
  border-radius: ${({ theme }) => theme.borderRadius.full};
  width: 3.4rem;
  height: 3.4rem;
  box-shadow: ${({ theme }) => theme.shadows.dropdown};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
`;

export const DeleteIconImg = styled.img`
  width: 1.7rem;
  height: 1.7rem;
`;
